import { z } from "zod";
import { uppercaseUlidSchema } from "./billing";
const timestampSchema = z.number().int().min(0).max(Number.MAX_SAFE_INTEGER);
const boundedIdSchema = z
    .string()
    .trim()
    .min(1)
    .max(128)
    .regex(/^[A-Za-z0-9._:-]+$/);
const cursorSchema = z
    .string()
    .regex(/^[A-Za-z0-9_-]+$/)
    .min(1)
    .max(256);
const reasonSchema = z.string().trim().min(8).max(1_000);
const safeTextSchema = z.string().trim().min(1).max(200);
export const adminUserRoleSchema = z.enum([
    "superadmin",
    "content_admin",
    "support_admin",
    "billing_admin",
    "auditor",
    "user",
]);
export const adminUserListQuerySchema = z.strictObject({
    q: z.string().trim().min(1).max(120).optional(),
    role: adminUserRoleSchema.optional(),
    cursor: cursorSchema.optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
});
export const adminTargetConfirmationSchema = z.strictObject({
    userId: boundedIdSchema,
    email: z.email(),
});
export const adminUserSummarySchema = z.strictObject({
    id: boundedIdSchema,
    name: z.string().min(1).max(200),
    email: z.email(),
    emailVerified: z.boolean(),
    role: adminUserRoleSchema,
    fullAccountBanned: z.boolean(),
    owner: z
        .strictObject({
        id: boundedIdSchema,
        status: z.enum(["pending", "active", "restricted", "deactivated"]),
        ageBand: z.enum(["adult", "minor", "unknown"]),
        preferredLocale: z.enum(["id", "en"]).nullable(),
    })
        .nullable(),
    activeBan: z
        .strictObject({
        id: uppercaseUlidSchema,
        createdByUserId: boundedIdSchema,
        createdAt: timestampSchema,
    })
        .nullable(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
});
export const adminUserListResponseSchema = z.strictObject({
    items: z.array(adminUserSummarySchema).max(50),
    nextCursor: cursorSchema.optional(),
});
export const adminSafeWebSessionSchema = z.strictObject({
    id: boundedIdSchema,
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
    expiresAt: timestampSchema,
    ipAddress: z.string().max(64).nullable(),
    userAgent: z.string().max(1024).nullable(),
    impersonatedBy: boundedIdSchema.nullable(),
});
export const adminSafeMcpGrantSchema = z.strictObject({
    grantId: boundedIdSchema,
    clientId: z.string().min(1).max(512),
    scopes: z.array(z.string().min(1).max(128)).max(50),
    createdAt: z.union([timestampSchema, z.string().min(1).max(64)]),
    expiresAt: z.union([timestampSchema, z.string().min(1).max(64)]).optional(),
});
export const adminUserDetailResponseSchema = z.strictObject({
    user: adminUserSummarySchema,
    sessions: z.array(adminSafeWebSessionSchema).max(50),
    mcpGrants: z.array(adminSafeMcpGrantSchema).max(50),
});
export const adminModerationActionInputSchema = z.strictObject({
    reason: reasonSchema,
    target: adminTargetConfirmationSchema,
});
export const adminRoleChangeInputSchema = z.strictObject({
    role: adminUserRoleSchema,
    reason: reasonSchema,
    target: adminTargetConfirmationSchema,
});
export const adminBanUserResponseSchema = z.strictObject({
    status: z.literal("banned"),
    banId: uppercaseUlidSchema,
    mcpGrantRevocation: z.strictObject({
        attempted: z.boolean(),
        revokedCount: z.number().int().min(0),
        partial: z.boolean(),
    }),
});
export const adminUnbanUserResponseSchema = z.strictObject({
    status: z.literal("unbanned"),
    liftedCount: z.number().int().min(1).max(1),
});
export const adminRevokeWebSessionsResponseSchema = z.strictObject({
    status: z.literal("revoked"),
    revokedCount: z.number().int().min(0),
});
export const adminRevokeMcpGrantsResponseSchema = z.strictObject({
    status: z.enum(["revoked", "partial"]),
    attemptedCount: z.number().int().min(0),
    revokedCount: z.number().int().min(0),
    reconnectRequired: z.boolean(),
});
export const adminSetRoleResponseSchema = z.strictObject({
    status: z.literal("updated"),
    previousRole: adminUserRoleSchema,
    role: adminUserRoleSchema,
});
export const adminUserActionErrorCodeSchema = z.enum([
    "unauthorized",
    "forbidden",
    "fresh_session_required",
    "invalid_request",
    "not_found",
    "target_confirmation_mismatch",
    "self_ban_denied",
    "self_demote_denied",
    "last_superadmin_denied",
    "already_banned",
    "not_banned",
    "oauth_dependency_failure",
]);
export const adminUserActionErrorResponseSchema = z.strictObject({
    error: z.strictObject({
        code: adminUserActionErrorCodeSchema.or(safeTextSchema),
        message: z.string(),
        requestId: z.string(),
    }),
});
//# sourceMappingURL=admin-users.js.map